import { useState } from "react";

import { notification } from "antd";

import prepareCertificateColumnData from "@Components/activity/helpers/prepareColumnData";
import { useCertificateTableDataQuery } from "@Components/activity/queries";
import { ICertificateTableData } from "@Components/activity/types";
import usePDFDownload from "@Components/shared/hooks/usePDFDownload";
import useStyledPDF from "@Components/shared/hooks/useStyledPDF";

import SessionManager from "@Core/session";
import { IPdfItem } from "@Models/IPdfItem";
import ActivityService from "@Services/ActivityService";
import { DateTime } from "@Utils";
import { PointsTypes } from "@Models/IPoints";

const useCertificationTableData = (activityInstanceIds: string[]) => {
    // ** State ** //
    const [activeDownloadId, setActiveDownloadId] = useState<string>();

    // ** Hooks ** //
    const { data, isHydrated, isFetching, isLoading } = useCertificateTableDataQuery(activityInstanceIds);

    const tableData = isHydrated ? data?.tableData ?? [] : [];
    const hasCPDReports = data?.hasCPDReports ?? false;
    const hasCMEReports = data?.hasCMEReports ?? false;

    const createStyledPDFReport = useStyledPDF({ report: "points", theme: "certificate" });
    const createStyledPDFReportCME = useStyledPDF({ report: "cme", theme: "certificate" });
    const download = usePDFDownload();

    // ** Variables ** //
    const service = new ActivityService();
    const { fullName, userId } = SessionManager.getServiceUser();

    const hasSomeReports = hasCPDReports || hasCMEReports;
    const hasBothReports = hasCPDReports && hasCMEReports;

    // ** Report ** //

    /**
     *  Prepares the file for download based on the points type
     */
    const prepareFile = async (tableDatum: ICertificateTableData) => {
        const isCPD = tableDatum.pointsType === "cpd";
        const isCME = tableDatum.pointsType === "cme";

        if (isCPD) {
            return await loadReport(tableDatum.id);
        } else if (isCME) {
            const template = prepareTemplate(fullName, tableDatum.points);
            return () => createStyledPDFReportCME({ content: template });
        }
    };

    /**
     * Downloads either a CPD or CME report
     * @param id The PointsMetadataId or PointId
     * @returns
     */
    const downloadReport = async (id: string, pointsType: PointsTypes) => {
        const tableDatum = tableData?.find((datum) => datum.id === id && pointsType === datum.pointsType);

        if (!tableDatum) return;

        const file = await prepareFile(tableDatum);

        if (!file) return;

        const fileName = `${tableDatum.pointsType.toUpperCase()} Points-${tableDatum.ownerName ?? fullName}-${DateTime(
            tableDatum.creationDate
        ).format("YYYY-MMM-DD")}.pdf`;

        setActiveDownloadId(id);

        await download({ file, fileName })
            .then(
                (success) =>
                    success &&
                    notification.success({
                        message: `Downloaded: ${fileName}.`,
                    })
            )
            .catch((err) => {
                // eslint-disable-next-line no-console
                console.error(err);
                notification.error({
                    message: "Unable to view the certificate.",
                });
            })
            .finally(() => setActiveDownloadId(undefined));
    };

    const columnData = prepareCertificateColumnData({
        activeDownloadId,
        isDisabled: !isHydrated || isFetching,
        downloadReport,
    });

    /**
     * Loads the report
     */
    const loadReport = async (pointsMetadataId: string) => {
        const result = await service.getPointsReport(pointsMetadataId, userId);

        if (result.hasErrors || !result.value) {
            notification.error({
                message: "Unable to view the certificate.",
            });
            return Promise.resolve(null);
        }

        const reportContent = !result.hasErrors ? result.value : [];

        return () => createStyledPDFReport({ content: reportContent });
    };

    return {
        tableData,
        columnData,
        certificationFlags: {
            isHydrated,
            isFetching,
            isLoading,
            hasCPDReports,
            hasCMEReports,
            hasSomeReports,
            hasBothReports,
        },
    };
};

/*
 * The template for the CME certificate
 */
const prepareTemplate = (fullName: string, pointsEarned: number): IPdfItem[] => [
    {
        style: "page",
        items: [
            {
                style: "centred",
                items: [
                    { style: "h2", value: "CERTIFICATE" },
                    { style: "h5", value: "OF" },
                    { style: "h4", value: "CONTINUING MEDICAL EDUCATION" },
                    { style: "h3", value: "CERTIFIES THAT" },
                    { style: "h1", value: fullName },
                    { style: "h5", value: "has been awarded" },
                    { style: "h3", value: `${pointsEarned} CME Points` },
                    {
                        style: "h5",
                        value: `for enduring activities summarised on their Inovus Medical TOTUM activity page\r\n generated on ${new Date().toLocaleDateString()}`,
                    },
                    { style: "spacer" },
                    {
                        style: "text",
                        value: "In support of improving patient care, this activity has been planned and implemented by Global Learning Collaborative (GLC) and Inovus Medical.  GLC is jointly accredited by the American Council for Continuing Medical Education (ACCME), the Accreditation Council for Pharmacy Education (ACPE), and the American Nurses Credentialing Center (ANCC) to provide continuing education for the healthcare team.",
                    },
                    { style: "spacer" },
                    {
                        value: `Global Learning Collaborative (GLC) designates this enduring activity for a maximum of ${pointsEarned}`,
                        style: "text",
                    },

                    {
                        value: "AMA PRA Category 1 Credit(s)™.",
                        style: "italic",
                    },
                    {
                        value: `Physicians should claim only the credit commensurate with the extent of their participation in the activity. \r\n Global Learning Collaborative (GLC) designates this activity for ${pointsEarned} nursing contact hours. Nurses should claim only the credit commensurate with the extent of their participation in the activity. Global Learning Collaborative (GLC) has been authorized by the American Academy of PAs (AAPA) to award AAPA Category 1 CME credit for activities planned in accordance with AAPA CME Criteria. Each activity and its designation for AAPA Category 1 CME credit(s) is highlighted on each module page. PAs should only claim credit commensurate with the extent of their participation.`,
                        style: "text",
                    },
                ],
            },
        ],
    },
];

export default useCertificationTableData;
